import React from 'react';

import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import PenIcon from '@atlaskit/icon/core/pen';

import type { RequestOptions, ResponseObject } from '../../../../utils/request/types';
import { createInsertAtCurrentPosition } from '../../actions/create-actions';
import { CONFIG_ITEM_KEYS } from '../../config-item-keys';
import type {
	EditorPluginAIConfigItemWithOptions,
	EditorPluginAIConfigItem,
} from '../../config-items';

type Environment = 'prod' | 'staging' | 'dev' | 'local';

const getKmsServiceUrl = (environment?: Environment): string => {
	switch (environment) {
		case 'dev':
			return 'https://kms-service.dev.atl-paas.net/rest/api/v1/case/summarize/escalation';
		case 'staging':
			return 'https://kms-service.staging.atl-paas.net/rest/api/v1/case/summarize/escalation';
		case 'prod':
			return 'https://kms-service.prod.atl-paas.net/rest/api/v1/case/summarize/escalation';
		default:
			return 'https://kms-service.dev.atl-paas.net/rest/api/v1/case/summarize/escalation';
	}
};

export const createEscalationNotes = (
	environment?: Environment,
	issueId?: string,
	userId?: string | null | undefined,
): EditorPluginAIConfigItem => ({
	key: CONFIG_ITEM_KEYS.GENERATE_ESCALATION_NOTES,
	title: aiConfigItemMessages.generateEscalationNotesTitle,
	description: aiConfigItemMessages.generateEscalationNotesDescription,
	icon: () => <PenIcon label="Escalation Notes Icon" />,
	endpoint: `/gateway/api/support/ex/jira/support-api-gateway/forge-proxy/1`,
	intentSchemaId: 'DISABLED',
	alwaysNoDocAndSelection: true,
	actions: {
		empty: [
			createInsertAtCurrentPosition({
				appearance: 'primary',
			}),
		],
		selection: [
			createInsertAtCurrentPosition({
				appearance: 'primary',
			}),
		],
	},
	getCustomHeaders: (): Record<string, string> => {
		if (!userId) {
			return {};
		}

		const kmsServiceUrl = getKmsServiceUrl(environment);

		return {
			'X-DOWNSTREAM-URL': kmsServiceUrl,
			'X-REGENERATE-DOWNSTREAM-ASAP-HEADER': 'true',
			AccountId: userId,
			accept: 'application/json',
			'Content-Type': 'application/json',
		};
	},
	getRequestPayload: () => {
		return {
			ticketKey: issueId,
		};
	},
	handleFetchResponse: (
		response: Response,
		requestOptions: RequestOptions,
		callback: (event: ResponseObject) => void,
		cacheKey: string,
	) => {
		const markdown = Object.entries(response)
			.map(([key, value]) => `**${key}**\n${value}\n`)
			.join('\n');

		callback({
			type: 'complete',
			markdown: markdown,
			cacheKey,
			modelInput: {
				selection: 'selection' in requestOptions ? requestOptions.selection : undefined,
				doc: 'doc' in requestOptions ? requestOptions.doc : undefined,
			},
			rovoActions: [],
		});
	},
});

export const createEscalationNotesWithOptions = (
	environment?: Environment,
	issueId?: string,
	userId?: string | null | undefined,
): EditorPluginAIConfigItemWithOptions => ({
	config: createEscalationNotes(environment, issueId, userId),
	triggers: {
		empty: {
			enabled: true,
			docMinSize: 0,
			docMaxSize: 0, // Only show when document is completely empty
		},
		selection: { enabled: false },
	},
});
